/**
 * The AI notice at contribution (TMMAPP-0142 U3 item 19; TMMAPI-0181 U6, MM-D-146). An allocation
 * of the person's own line into a virtual account they don't own answers `aiNoticeRequired: true`
 * and `aiNoticeVersion` until they acknowledge the notice (`POST /v1/me/consents {kind:
 * AI_CONTRIBUTIONS, version}`). The allocation has already succeeded; the notice never blocks it.
 *
 * The three allocate answers are typed (`AllocationLineAnswer`, `AllocationBulkAnswer`: `allOf` the
 * body and `AllocationAiNotice`, TMMAPI-0182 U2, J4). `allocations.ts` hands each answer here, and
 * the version is held for the shell's one notice (`AiContributionNotice`). Nothing is stored in the
 * browser.
 */
import type { AllocationAiNotice } from './generated';

/** The version to acknowledge, when the answer asks for the notice; null otherwise. */
export function readAiNotice(answer: AllocationAiNotice): string | null {
  if (!answer.aiNoticeRequired) return null;
  const version = answer.aiNoticeVersion ?? null;
  return version !== null && version.length > 0 ? version : null;
}

type Listener = () => void;

let pendingVersion: string | null = null;
// Versions shown in this visit: the notice shows once, and a later allocation doesn't repeat it.
const shown = new Set<string>();
const listeners = new Set<Listener>();

function emit() {
  for (const listener of listeners) listener();
}

export const aiContributionNotice = {
  /** An allocate answer: holds the notice's version when it asks for the notice. */
  report(answer: AllocationAiNotice): void {
    const version = readAiNotice(answer);
    if (version === null || shown.has(version)) return;
    shown.add(version);
    pendingVersion = version;
    emit();
  },
  /** The notice was answered (OK) or closed. */
  clear(): void {
    if (pendingVersion === null) return;
    pendingVersion = null;
    emit();
  },
  snapshot(): string | null {
    return pendingVersion;
  },
  /** Test-only: forget what this visit showed. */
  resetForTests(): void {
    pendingVersion = null;
    shown.clear();
  },
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};
